import { FC } from 'react';
import { Card } from '../components/Card';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { sortByNewest } from '../utils/arrays';
import { IonButton, IonButtons, IonSkeletonText, IonBadge } from '@ionic/react';
import { ICaremapNoteResponse } from '../notes/model/NotesClient';
import { IRecording, RecordingState } from '../appointments/recording/RecordingState';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';
import { Text } from '../components/Text';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { NotesModel } from '../notes/model/NotesModel';
import { CareMapNotesListItem } from '../notes/note-list/CareMapNotesListItem';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { OfflineRecordingListItem } from '../appointments/recording/OfflineRecordingListItem';
import { RecordingEmptyState } from '../appointments/recording/RecordingEmptyState';
import { DevSettings } from '../support/DevSettings';
import { RecordingDevTools } from '../appointments/recording/RecordingDevTools';


export const Recordings: FC = () => {
    const history = useInstance(HistoryViewModel);
    const recordingState = useInstance(RecordingState);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const careMapState = useInstance(CareMapState);
    const devSettings = useInstance(DevSettings);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    // Subscribe to recording state to get offline recordings
    const recordingStateData = useSubscription(() => recordingState);
    const offlineRecordings = recordingStateData?.offlineRecordings || [];

    // TODO: forActiveCaremap should maybe return underined or something instead of throwing an error?
    //      we want this to show up if the user is anonymous still
    let notesModel: NotesModel;
    try {
        notesModel = notesModelFactory.forActiveCaremap();
    } catch (e) {
        // no active caremap
        return <Card padded><RecordingEmptyState /></Card>;
    }

    const numToShow = 3;
    const sortedNotes: ICaremapNoteResponse[] = sortByNewest(notesModel.getNotes().filter(n => n.otherAttributes.recording), 'updatedAt');

    // Sort offline recordings by creation date (newest first)
    const sortedOfflineRecordings = sortByNewest(offlineRecordings, 'created');

    return (
        <>
            {careMapState.getCareMaps().length > 1 &&
                <div style={{margin: '10px 10px 20px'}}>
                    <CareRecipientSwitcher />
                </div>
            }

            {/* Recording Dev Tools */}
            {(devSettings.simulateRecordingSuccess || devSettings.enableUploadRecording) && (
                <RecordingDevTools />
            )}

            <Card >
                {/* Show offline recordings section */}
                {sortedOfflineRecordings.length > 0 && (
                    <>
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingTop: 16}}>
                            <div style={{paddingLeft: 16, display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <Text><b>Offline Recordings</b></Text>
                                <IonBadge color="warning" style={{ fontSize: '10px', minWidth: '16px', height: '16px' }}>
                                    {sortedOfflineRecordings.length}
                                </IonBadge>
                            </div>
                        </div>
                        {sortedOfflineRecordings.map((recording) => (
                            <OfflineRecordingListItem
                                key={recording.id}
                                recording={recording}
                                showBottomLine={true}
                            />
                        ))}
                    </>
                )}

                {sortedNotes.length > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingTop: sortedOfflineRecordings.length > 0 ? 36 : 16}}>
                        <div style={{paddingLeft: 16}}><Text><b>Recent Summaries</b></Text></div>
                        <IonButtons slot="end">
                            <div style={{marginRight: 12}}>
                                <IonButton
                                    fill="clear"
                                    color="primary"
                                    data-id="view-all-recordings"
                                    onClick={() => history.push(`/notes/${careMapState.getActiveCaremap()?.careMapId}`)}
                                >
                                    View All
                                </IonButton>
                            </div>
                        </IonButtons>
                    </div>
                )}

                {recordingState.isRecording() || notesModel.hasLoaded ? (sortedNotes.length > 0 || sortedOfflineRecordings.length > 0) ? sortedNotes.slice(0, numToShow).map((note: ICaremapNoteResponse, idx) => {
                    const recording = note.otherAttributes.recording as IRecording;
                    const uploaded = recordingState.isUploadedStatus(recording.status);
                    const hasTranscription = Boolean(recording.transcription);
                    const isClickable = uploaded || (recording.status === 'recording-complete' && hasTranscription);
                    return (
                        <CareMapNotesListItem
                            note={note}
                            key={note.noteId}
                            button={isClickable}
                            detail={isClickable}
                            showPrivacy={false}
                            showNoteTargetHeader
                            lines={idx === numToShow-1 ? 'none' : undefined}
                            onClick={isClickable ? () => viewNoteDrawer.open({ noteId: note.noteId, trackingFrom: 'Recent Recordings' }) : undefined}
                        />
                    );
                }) : (
                    <RecordingEmptyState />
                ) : (
                    <div style={{margin: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                        {[1,2,3].map(i => (
                            <IonSkeletonText key={i} animated style={{'--border-radius': '8px', height: 75}}></IonSkeletonText>
                        ))}
                    </div>
                )}
            </Card>
        </>
    );
};
